/**
 * ตัวสร้าง identifier
 *
 * ใช้ ULID เป็นหลักเพราะเรียงตามเวลาได้ ทำให้ index บน primary key ไม่กระจัดกระจาย
 * เวลาเขียนจำนวนมาก และใช้เป็น cursor ของ pagination ได้โดยตรง
 *
 * ข้อห้าม: ห้ามนำเลขทะเบียนลูกค้าหรือข้อมูลส่วนบุคคลมาเป็นส่วนหนึ่งของ id หรือชื่อไฟล์
 */

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32
const ENCODING_LEN = ENCODING.length;
const TIME_LEN = 10;
const RANDOM_LEN = 16;

function encodeTime(now: number): string {
  let out = '';
  let value = now;
  for (let i = TIME_LEN - 1; i >= 0; i--) {
    const mod = value % ENCODING_LEN;
    out = ENCODING[mod] + out;
    value = (value - mod) / ENCODING_LEN;
  }
  return out;
}

function encodeRandom(): string {
  const bytes = new Uint8Array(RANDOM_LEN);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < RANDOM_LEN; i++) {
    out += ENCODING[bytes[i]! % ENCODING_LEN];
  }
  return out;
}

export function ulid(now: number = Date.now()): string {
  return encodeTime(now) + encodeRandom();
}

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function isUlid(value: unknown): value is string {
  return typeof value === 'string' && ULID_RE.test(value);
}

/** ดึงเวลาที่ฝังอยู่ใน ULID กลับมาเป็น epoch ms */
export function ulidTime(id: string): number | null {
  if (!isUlid(id)) return null;
  let time = 0;
  for (const char of id.slice(0, TIME_LEN)) {
    const index = ENCODING.indexOf(char);
    if (index < 0) return null;
    time = time * ENCODING_LEN + index;
  }
  return time;
}

export function uuid(): string {
  return crypto.randomUUID();
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const IMAGE_EXTENSIONS = ['jpg', 'webp', 'png'];

/**
 * สร้าง object key ของรูปใน R2
 *   captures/<yyyy>/<mm>/<captureId>/<uuid>.<ext>
 *
 * - ไม่มีเลขทะเบียนลูกค้า ชื่อผู้ใช้ หรือข้อมูลส่วนบุคคลใดในเส้นทาง
 * - client กำหนดเองไม่ได้ server เป็นผู้สร้างเสมอ
 * - แบ่งตามปีและเดือนเพื่อให้ตั้ง lifecycle rule และไล่หา orphan object ได้ง่าย
 */
export function buildImageObjectKey(
  captureId: string,
  contentType: string,
  at = new Date(),
): string {
  const ext = contentType === 'image/webp' ? 'webp' : contentType === 'image/png' ? 'png' : 'jpg';
  const yyyy = at.getUTCFullYear();
  const mm = String(at.getUTCMonth() + 1).padStart(2, '0');
  return 'captures/' + yyyy + '/' + mm + '/' + captureId + '/' + uuid() + '.' + ext;
}

/** ตรวจว่า object key เป็นของ capture นี้จริง กัน IDOR และ path traversal */
export function isImageKeyOwnedBy(objectKey: string, captureId: string): boolean {
  if (typeof objectKey !== 'string' || objectKey.includes('..') || objectKey.startsWith('/')) {
    return false;
  }
  if (!isUlid(captureId)) return false;

  const parts = objectKey.split('/');
  if (parts.length !== 5) return false;

  const prefix = parts[0]!;
  const year = parts[1]!;
  const month = parts[2]!;
  const id = parts[3]!;
  const file = parts[4]!;

  if (prefix !== 'captures' || id !== captureId) return false;
  if (!/^[0-9]{4}$/.test(year) || !/^[0-9]{2}$/.test(month)) return false;

  const dot = file.lastIndexOf('.');
  if (dot < 0) return false;
  return UUID_RE.test(file.slice(0, dot)) && IMAGE_EXTENSIONS.includes(file.slice(dot + 1));
}

/** prefix ของ object ทั้งหมดที่เป็นของ capture หนึ่ง ใช้ตอนลบหรือหา orphan */
export function imageKeyPrefixFor(captureId: string, createdAt: Date): string {
  const yyyy = createdAt.getUTCFullYear();
  const mm = String(createdAt.getUTCMonth() + 1).padStart(2, '0');
  return 'captures/' + yyyy + '/' + mm + '/' + captureId + '/';
}
